import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Input,
  Button,
  Rate,
  Divider,
  message,
  Card,
  Row,
  Col,
  Progress,
  Typography,
  Space,
  Collapse,
  Tabs,
  Spin,
  List,
} from "antd";
import axios from "axios";
import Cookies from "js-cookie";
import { firebaseAPI } from "../firebaseConfig";
import { sha256 } from "hash.js";
import { BeatLoader } from "react-spinners";
import { css } from "@emotion/react";
import {
  FilePdfOutlined,
  VideoCameraOutlined,
  FileWordOutlined,
  StarOutlined,
  StarFilled,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;

const override = css`
  display: block;
  margin: 0 auto;
  border-color: red;
`;

function hashString(input) {
  return sha256().update(input).digest("hex");
}
const videoCriteria = [
  {
    key: "language",
    title: "Language Skills",
    options: [
      { stars: 1, label: "Poor, difficult to follow" },
      { stars: 3, label: "Fluent, structured communication" },
      { stars: 5, label: "Perfect + bonus languages" },
    ],
    weight: 0.2,
  },
  {
    key: "eagerness",
    title: "Eagerness to Learn & Passion for Technology",
    options: [
      { stars: 1, label: "No clear interest" },
      { stars: 3, label: "Shows some enthusiasm" },
      { stars: 5, label: "Perfect + personal initiatives" },
    ],
    weight: 0.2,
  },
  {
    key: "exposure",
    title: "Technical & Corporate Exposure",
    options: [
      { stars: 1, label: "No hands-on experience" },
      { stars: 3, label: "Some certifications or academic projects" },
      { stars: 5, label: "Perfect + deep impact" },
    ],
    weight: 0.2,
  },
  {
    key: "motivation",
    title: "Motivation",
    options: [
      { stars: 1, label: "Unclear drive" },
      { stars: 3, label: "Reasonable goals" },
      { stars: 5, label: "Perfect + clear long-term vision" },
    ],
    weight: 0.2,
  },
  {
    key: "extracurricular",
    title: "Extracurricular Involvement",
    options: [
      { stars: 1, label: "No involvement" },
      { stars: 3, label: "Some team activities" },
      { stars: 5, label: "Perfect + leadership roles" },
    ],
    weight: 0.2,
  },
];
const InterviewCriteria = [
  {
    key: "introduction",
    title:
      "Briefly introduce yourself and tell us what made you apply to the Dell Summer Academy",
    options: [
      { stars: 1, label: "Unclear introduction or weak motivation" },
      { stars: 2, label: "Clear communication with reasonable motivation" },
      { stars: 3, label: "Engaging intro with strong, personal motivation" },
    ],
    weight: 0.1,
  },
  {
    key: "projectAchievement",
    title:
      "Tell us about a project or achievement you’re proud of—what was your role?",
    options: [
      { stars: 1, label: "Limited ownership or unclear role" },
      { stars: 2, label: "Adequate role clarity and effort" },
      { stars: 3, label: "Strong ownership and high impact" },
    ],
    weight: 0.1,
  },
  {
    key: "selfLearning",
    title: "What’s something you’ve taught yourself recently, and why?",
    options: [
      { stars: 1, label: "Minimal self‑learning or unclear reason" },
      { stars: 2, label: "Solid example and reasonable choice" },
      { stars: 3, label: "Ambitious learning with clear purpose" },
    ],
    weight: 0.1,
  },
  {
    key: "conflictResolution",
    title: "Tell us about a conflict in a team project and how you handled it",
    options: [
      { stars: 1, label: "Poor handling or lack of insight" },
      { stars: 2, label: "Adequate resolution and empathy" },
      { stars: 3, label: "Excellent leadership and maturity" },
    ],
    weight: 0.1,
  },
  {
    key: "adaptability",
    title:
      "Give a real situation where you were given a task you’d never done—how did you approach it?",
    options: [
      { stars: 1, label: "Struggled to adapt or lacked structure" },
      { stars: 2, label: "Reasonable plan and learning" },
      { stars: 3, label: "Proactive, structured, and successful" },
    ],
    weight: 0.1,
  },
  {
    key: "researchEffort",
    title:
      "What do you know about Dell Technologies? Any product or initiative that caught your attention?",
    options: [
      { stars: 1, label: "Little to no prior research" },
      { stars: 2, label: "Some relevant knowledge" },
      { stars: 3, label: "In‑depth insight and genuine enthusiasm" },
    ],
    weight: 0.1,
  },
  {
    key: "strengths",
    title: "Tell us about three of your strengths",
    options: [
      { stars: 1, label: "Vague or generic strengths" },
      { stars: 2, label: "Clear, relevant strengths" },
      { stars: 3, label: "Strong self‑awareness and examples" },
    ],
    weight: 0.1,
  },
  {
    key: "weaknesses",
    title:
      "Tell us about two weaknesses or development areas and how you tackle them",
    options: [
      { stars: 1, label: "Unaware or no plan for improvement" },
      { stars: 2, label: "Aware with some action plan" },
      { stars: 3, label: "Deep insight and clear growth steps" },
    ],
    weight: 0.1,
  },
  {
    key: "programAlignment",
    title: "From past internships, what do you hope to gain from this program?",
    options: [
      { stars: 1, label: "Misaligned or unclear goals" },
      { stars: 2, label: "Reasonable alignment with program" },
      { stars: 3, label: "Strong, insightful alignment" },
    ],
    weight: 0.1,
  },
  {
    key: "questionsForUs",
    title: "Do you have any questions for us?",
    options: [
      { stars: 1, label: "No questions or very basic" },
      { stars: 2, label: "Relevant, thoughtful questions" },
      { stars: 3, label: "Deep, strategic curiosity" },
    ],
    weight: 0.1,
  },
];

// Evaluation Components
const DefaultEvaluation = ({
  scores,
  handleScoreChange,
  calculateTotalScore,
  criteria,
  starsCount,
}) => (
  <Card
    title="Evaluate Submission: Summer Academy"
    style={{ marginBottom: 24 }}
    headStyle={{
      backgroundColor: "#fafafa",
      borderBottom: "1px solid #e8e8e8",
    }}
    bodyStyle={{ padding: "24px" }}
  >
    {criteria.map((crit, index) => (
      <div key={crit.key} style={{ marginBottom: "2rem" }}>
        <Text
          strong
          style={{ fontSize: 16, display: "block", marginBottom: 8 }}
        >
          {crit.title}
        </Text>
        <List
          size="small"
          dataSource={crit.options}
          renderItem={(opt) => (
            <List.Item style={{ padding: "8px 12px", marginBottom: 4 }}>
              <Space align="center">
                <Rate
                  disabled
                  defaultValue={opt.stars}
                  count={starsCount}
                  style={{ fontSize: 16, color: "#faad14", marginRight: 8 }}
                  character={<StarOutlined />}
                />
                <Text>{opt.label}</Text>
              </Space>
            </List.Item>
          )}
          style={{ marginBottom: 12 }}
        />
        <style>
          {`
.custom-rate .ant-rate-star:not(.ant-rate-star-full) svg {
  stroke: black; /* Outline color for unselected stars */
  stroke-width: 30;
}

.custom-rate .ant-rate-star-full svg {
  fill: #faad14; /* Filled star color (selected) */
}

`}
        </style>
        <Rate
          className="custom-rate"
          allowClear={true}
          tooltips={crit.options.map((o) => o.label)}
          onChange={(value) => handleScoreChange(value, index)}
          value={scores[index]}
          count={starsCount}
          style={{ fontSize: 28, color: "#faad14" }}
          character={<StarFilled />}
        />
      </div>
    ))}
    <Divider />
    <div style={{ textAlign: "center", marginTop: 16 }}>
      <Title level={4}>Total Score</Title>
      <Progress
        type="circle"
        percent={calculateTotalScore()}
        width={80}
        format={(percent) => `${percent}%`}
        strokeColor={{
          "0%": "#108ee9",
          "100%": "#87d068",
        }}
      />
    </div>
  </Card>
);

// Main Component
const EvaluateSubmission = () => {
  const { submissionId } = useParams();
  const location = useLocation();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState("");
  const [formScore, setFormScore] = useState(0);
  const [activeTab, setActiveTab] = useState("questions");
  const [criteria, setCriteria] = useState(InterviewCriteria);
  const [starsCount, setStarsCount] = useState(3);

  const [mediaFiles, setMediaFiles] = useState({
    cv: null,
    video: null,
    docx: null,
  });

  const defaultCriteriaDescriptions = {
    "Language Skills": 0.2,
    "Eager to Learn": 0.2,
    "Passion about technology": 0.3,
    Motivation: 0.3,
  };

  const [criteriaDescriptions, setCriteriaDescriptions] = useState(
    defaultCriteriaDescriptions
  );

  const [scores, setScores] = useState(
    Array(Object.keys(criteria).length).fill(0)
  );

  const [totalScore, setTotalScore] = useState(0);

  const program = decodeURI(location.pathname.split("/")[1]);

  useEffect(() => {
    const fetchSubmission = async () => {
      try {
        let token = Cookies.get("token") || "";
        const response = await axiosInstance.get(
          `${firebaseAPI}/submissions/${submissionId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSubmission(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching submission:", error);
        setLoading(false);
      }
    };
    fetchSubmission();
  }, [submissionId]);

  useEffect(() => {
    if (submission) {
      setScore(submission.judgeScore || 0);
      setComment(submission.judgeComment || "");
      setScores(submission.judgeScores || []);

      // Weighted score for default criteria
      // Ensure submission.judgeScores and criteria are not null/undefined and are arrays
      const weighted =
        Array.isArray(submission.judgeScores) && Array.isArray(criteria)
          ? submission.judgeScores.reduce(
              (acc, score, idx) =>
                acc + (score || 0) * (criteria[idx]?.weight || 0), // Pull weight from criteria array, handle null score/weight
              0
            )
          : 0; // Default to 0 if judgeScores or criteria are not valid arrays

      // Since weights sum to 1, multiply by 20 to convert 1–5 scale into 0–100
      setTotalScore((weighted * 20).toFixed(2));

      let correctSum = 0;
      // Ensure submission.form and submission.form.questions are not null/undefined and are arrays
      if (
        submission.form?.questions &&
        Array.isArray(submission.form.questions)
      ) {
        submission.form.questions.forEach((question, index) => {
          // Use optional chaining for submission.answers[index]
          const answer = submission.answers?.[index]?.questionAnswer;

          if (!question.questionCorrectAnswer && answer) {
            correctSum++;
          } else if (answer === question.questionCorrectAnswer) {
            correctSum++;
          }
        });
      }
      setFormScore(correctSum);

      // Extract media files
      // Use optional chaining for participantProfile and its properties
      const cv = submission.participantProfile?.cvUrl;
      const video = submission.answers?.find(
        (a) => a?.questionType === "Upload Video"
      )?.questionAnswer;
      const docx = submission.answers?.find(
        (a) =>
          a?.questionType === "Upload File" &&
          a?.questionAnswer?.endsWith(".docx")
      )?.questionAnswer;

      setMediaFiles({
        cv: cv
          ? { url: cv, type: cv.endsWith(".docx") ? "docx" : "pdf" }
          : null,
        video: video ? { url: video, type: "video" } : null,
        docx: docx ? { url: docx, type: "docx" } : null,
      });
    }
  }, [submission, criteria]);

  useEffect(() => {
    if (program.toLowerCase().includes("envision the future")) {
      setCriteriaDescriptions([
        "The importance and timeliness of the problem...",
        "The practicality of the proposed solution...",
        "The creativity and innovation of the solution",
        "The suitability of the proposed development platform...",
        "The understanding and coverage of the relevant project management...",
        "The presentation skills of the team...",
        "Your overall assessment as a whole",
      ]);
      setScores(Array(7).fill(0));
    } else {
      setCriteriaDescriptions(defaultCriteriaDescriptions);
      setScores(Array(Object.keys(defaultCriteriaDescriptions).length).fill(0));
    }
  }, [program]);

  const handleScoreChange = (value, index) => {
    const newScores = [...scores];
    newScores[index] = value;
    setScores(newScores);
    const weighted = newScores.reduce(
      (acc, score, idx) =>
        acc +
        (typeof score === "number" && !isNaN(score) ? score : 0) *
          (criteria && criteria[idx] && typeof criteria[idx].weight === "number"
            ? criteria[idx].weight
            : 0),
      0
    );
    const newScore = ((weighted * 100) / starsCount).toFixed(2);
    setTotalScore(newScore);
  };

  const calculateTotalScore = () => totalScore;

  const handleSubmitEvaluation = async () => {
    try {
      let token = Cookies.get("token") || "";
      await axiosInstance.post(
        `${firebaseAPI}/submissions/updateJudgeScore`,
        {
          submissionId,
          judgeScore: calculateTotalScore(),
          judgeScores: scores,
          judgeComment: comment || "",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      message.success("Evaluation submitted successfully!", 1, () => {
        const role = Cookies.get("role") || "";
        const redirectPath =
          role === hashString("admin")
            ? `/${program}/overview`
            : `/${program}/judgehome`;
        window.location.href = redirectPath;
      });
    } catch (error) {
      console.error("Error updating judge score:", error);
      message.error("Failed to submit evaluation. Please try again.");
    }
  };

  const handleBackToList = () => {
    const role = Cookies.get("role") || "";
    const redirectPath =
      role === hashString("admin")
        ? `/${program}/overview`
        : `/${program}/judgehome`;
    window.location.href = redirectPath;
  };

  const isImageUrl = (url) => {
    return /^https?:\/\/.*\.(jpeg|jpg|gif|png)/i.test(url);
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <BeatLoader
          color="#1890ff"
          loading={loading}
          css={override}
          size={25}
        />
      </div>
    );
  }

  if (!submission) {
    return (
      <Card style={{ margin: 24, textAlign: "center" }}>
        <Title level={4}>Submission not found</Title>
        <Button type="primary" onClick={handleBackToList}>
          Back to Submissions
        </Button>
      </Card>
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <Card
            title={`Submission Review - ${submission.user?.name || "Unknown"}`}
            extra={
              <Progress
                percent={(formScore / submission.form.questions.length) * 100}
                format={() =>
                  `${formScore}/${submission.form.questions.length}`
                }
                status={
                  formScore === submission.form.questions.length
                    ? "success"
                    : "normal"
                }
              />
            }
          >
            <Text style={{ display: "block", marginBottom: 16 }}>
              Please evaluate the candidate based on the following criteria for
              the Summer Academy program. Assign a score from 0 to 3 for each
              criterion, where:
              <br />0 = Poor (0%) &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp; 1 = Fair
              (33%) &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp; 2 = Good (66%)
              &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp; 3 = Excellent (100%)
            </Text>

            <Tabs activeKey={activeTab} onChange={setActiveTab}>
              <TabPane tab="Questions" key="questions">
                <Collapse accordion defaultActiveKey={["0"]}>
                  {submission.form.questions.map((question, index) => {
                    const answer = submission.answers[index];
                    return (
                      <Panel
                        header={`Question ${index + 1}`}
                        key={index}
                        extra={
                          question.questionCorrectAnswer && (
                            <Text
                              type={
                                answer?.questionAnswer ===
                                question.questionCorrectAnswer
                                  ? "success"
                                  : "danger"
                              }
                            >
                              {answer?.questionAnswer ===
                              question.questionCorrectAnswer
                                ? "✔ Correct"
                                : "✘ Incorrect"}
                            </Text>
                          )
                        }
                      >
                        <div style={{ marginBottom: 16 }}>
                          <Text strong>{question.questionContent}</Text>
                        </div>
                        {question.questionImage && (
                          <img
                            src={question.questionImage}
                            alt={`Question ${index + 1}`}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "300px",
                              borderRadius: "8px",
                            }}
                          />
                        )}
                        {!question.questionCorrectAnswer && (
                          <Text type="secondary" italic>
                            There is no correct answer for this question
                          </Text>
                        )}
                        {answer && (
                          <div style={{ marginTop: 16 }}>
                            {question.questionType === "Multiple Choice" && (
                              <div>
                                <Text strong>Provided Answer:</Text>
                                {isImageUrl(answer.questionAnswer) ? (
                                  <img
                                    src={answer.questionAnswer}
                                    alt="Provided Answer"
                                    style={{
                                      maxWidth: "100%",
                                      maxHeight: "300px",
                                      borderRadius: "8px",
                                      border:
                                        answer.questionAnswer ===
                                        question.questionCorrectAnswer
                                          ? "2px solid #52c41a"
                                          : "2px solid #ff4d4f",
                                      padding: "4px",
                                    }}
                                  />
                                ) : (
                                  <Text
                                    style={{
                                      display: "block",
                                      marginTop: 4,
                                      color:
                                        answer.questionAnswer ===
                                        question.questionCorrectAnswer
                                          ? "#52c41a"
                                          : "#ff4d4f",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {answer.questionAnswer}
                                  </Text>
                                )}
                              </div>
                            )}
                            {question.questionType === "Upload File" && (
                              <Button
                                type="link"
                                href={answer.questionAnswer}
                                target="_blank"
                                icon={<FilePdfOutlined />}
                              >
                                View Uploaded File
                              </Button>
                            )}
                            {question.questionType === "Upload Video" && (
                              <Button
                                type="link"
                                href={answer.questionAnswer}
                                target="_blank"
                                icon={<VideoCameraOutlined />}
                              >
                                View Video Submission
                              </Button>
                            )}
                            {![
                              "Upload File",
                              "Upload Video",
                              "Multiple Choice",
                            ].includes(question.questionType) && (
                              <Card
                                bordered
                                style={{
                                  marginTop: 16,
                                  backgroundColor: "#fafafa",
                                }}
                              >
                                <Text>{answer.questionAnswer}</Text>
                              </Card>
                            )}
                          </div>
                        )}
                      </Panel>
                    );
                  })}
                </Collapse>
              </TabPane>

              {mediaFiles.cv && (
                <TabPane
                  tab={
                    <span>
                      {mediaFiles.cv.type === "pdf" ? (
                        <FilePdfOutlined />
                      ) : (
                        <FileWordOutlined />
                      )}
                      CV
                    </span>
                  }
                  key="cv"
                >
                  <div style={{ padding: "20px", textAlign: "center" }}>
                    <Button
                      type="primary"
                      href={mediaFiles.cv.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      icon={
                        mediaFiles.cv.type === "pdf" ? (
                          <FilePdfOutlined />
                        ) : (
                          <FileWordOutlined />
                        )
                      }
                      size="large"
                    >
                      View CV
                    </Button>
                  </div>
                </TabPane>
              )}

              {mediaFiles.video && (
                <TabPane
                  tab={
                    <span>
                      <VideoCameraOutlined />
                      Video Submission
                    </span>
                  }
                  key="video"
                >
                  <div style={{ padding: "20px", textAlign: "center" }}>
                    <Button
                      type="primary"
                      href={mediaFiles.video.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      icon={<VideoCameraOutlined />}
                      size="large"
                    >
                      View Video
                    </Button>
                  </div>
                </TabPane>
              )}

              {mediaFiles.docx && (
                <TabPane
                  tab={
                    <span>
                      <FileWordOutlined />
                      DOCX File
                    </span>
                  }
                  key="docx"
                >
                  <div style={{ padding: "20px", textAlign: "center" }}>
                    <Button
                      type="primary"
                      href={mediaFiles.docx.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      icon={<FileWordOutlined />}
                      size="large"
                    >
                      View DOCX
                    </Button>
                  </div>
                </TabPane>
              )}
            </Tabs>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <DefaultEvaluation
            criteriaDescriptions={criteriaDescriptions}
            scores={scores}
            handleScoreChange={handleScoreChange}
            calculateTotalScore={calculateTotalScore}
            criteria={criteria}
            starsCount={starsCount}
          />

          <Card title="Evaluation Comments" style={{ marginBottom: 24 }}>
            <Input.TextArea
              placeholder="Add your comments (optional)"
              value={comment}
              rows={5}
              onChange={(e) => setComment(e.target.value)}
              style={{ marginBottom: 16 }}
            />
            <Space>
              <Button onClick={handleBackToList}>Back to List</Button>
              <Button
                type="primary"
                onClick={handleSubmitEvaluation}
                disabled={!scores.some((score) => score > 0)}
              >
                Submit Evaluation
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default EvaluateSubmission;
